// Per-user progress persistence. Reads globalThis.localStorage (not window)
// so this module is testable without a DOM, and every access degrades to
// in-memory rather than throwing (private browsing, blocked site data).
//
// Everything this module holds is a cache. Discarding it is always safe —
// it costs a round trip and nothing else. That is why every failure path here
// returns null instead of trying to repair anything.

import { isLangId } from "@aybuben/core/lang.ts";
import { isVersion } from "@aybuben/core/onboarding/version.ts";
import { parseEvent, type ProgressEvent } from "@aybuben/core/progress/event.ts";
import { contentOf } from "@aybuben/core/progress/fold-key.ts";
import { fold } from "@aybuben/core/progress/fold.ts";
import { isTreeName } from "@aybuben/core/progress/tree-name.ts";
import type { GameState } from "@aybuben/core/state.ts";
import { defaultState } from "@aybuben/core/storage.ts";
import { isLayoutId } from "@aybuben/core/writing/layouts.ts";

import type { AuthUser } from "../auth/source.ts";

export const LOCAL_VERSION = 6;

/** Per account; survives sign-out. */
export const localKey = (userId: string): string => `aybuben:v${LOCAL_VERSION}:${userId}`;

/** The remembered session the bootstrap falls back on. Written on sign-in, cleared on sign-out. */
export const SESSION_KEY = "aybuben:session";

export interface LocalProgress {
	version: typeof LOCAL_VERSION;
	/**
	 * The `foldKey` the snapshot was folded under: content version and fold
	 * rules. A mismatch discards the snapshot; the remote snapshot's `content`
	 * column holds the same key for the same reason.
	 */
	content: string;
	/** The current folded state — `record` keeps this up to date synchronously. */
	snapshot: GameState;
	/** Server watermark: the event row id this snapshot folds through. */
	coversUpTo: string | null;
	/** Recorded, not yet pushed. */
	tail: ProgressEvent[];
}

const read = (k: string): string | null => {
	try {
		return globalThis.localStorage?.getItem(k) ?? null;
	} catch {
		return null;
	}
};
const write = (k: string, v: string): void => {
	try {
		globalThis.localStorage?.setItem(k, v);
	} catch {
		/* memory only this session */
	}
};
const drop = (k: string): void => {
	try {
		globalThis.localStorage?.removeItem(k);
	} catch {
		/* nothing to do */
	}
};

const isObj = (x: unknown): x is Record<string, unknown> =>
	typeof x === "object" && x !== null && !Array.isArray(x);

const isSlips = (x: unknown): boolean =>
	isObj(x) && Object.values(x).every((t) => typeof t === "number" && Number.isFinite(t));

/**
 * A stored state, merged onto the current defaults.
 *
 * `lang` is the one field that reaches CATALOGS/LOCALES lookups which neither
 * throw at construction nor guard the lookup — a hand-edited value would crash
 * the first render with no reachable reset UI. isLangId is lang.ts's
 * ready-made validator for exactly this boundary.
 *
 * Exported: sync/appwrite.ts's `pullSnapshot` faces the exact same trust
 * boundary against the remote `state` column (a snapshot is a cache,
 * whether local or remote, and this is the one place that coercion is
 * written), so it reuses this rather than a second validator drifting from it.
 */
export function coerceState(raw: unknown): GameState | null {
	if (!isObj(raw)) return null;
	const base = defaultState();
	// Only the fields this build knows. A snapshot written before xp and streaks
	// were retired still carries them, and spreading it would keep them forever.
	const copy: Record<string, unknown> = {};
	for (const k of Object.keys(base)) if (k in raw) copy[k] = raw[k];
	if (copy.lang !== undefined && (typeof copy.lang !== "string" || !isLangId(copy.lang)))
		delete copy.lang;
	if (copy.onboarded !== undefined && !isVersion(copy.onboarded)) delete copy.onboarded;
	// Practice reads every value as a time; one string would make a key a mistake forever.
	if (copy.slips !== undefined && !isSlips(copy.slips)) delete copy.slips;
	if (copy.layout !== undefined && !isLayoutId(copy.layout)) delete copy.layout;
	if (copy.kbdHinted !== undefined && typeof copy.kbdHinted !== "boolean") delete copy.kbdHinted;
	if (copy.treeName !== undefined && !isTreeName(copy.treeName)) delete copy.treeName;
	// Every key came from defaultState() and lang was validated above.
	return { ...base, ...copy } as GameState;
}

export function loadLocal(userId: string, content: string): LocalProgress | null {
	const raw = read(localKey(userId));
	if (!raw) return null;
	let parsed: unknown;
	// Trust boundary: JSON.parse returns `any` and promises nothing.
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}
	if (!isObj(parsed)) return null;
	if (parsed.version !== LOCAL_VERSION) return null;
	const tail: ProgressEvent[] = [];
	if (Array.isArray(parsed.tail)) {
		for (const e of parsed.tail) {
			const one = parseEvent(e);
			// A tail entry we cannot read is dropped, not fatal: the rest of the
			// tail is still real progress the learner earned.
			if (one) tail.push(one);
		}
	}
	// A snapshot folded under other content is not wrong, it is answering a
	// different question — discard it. The tail is different: it is progress not
	// yet synced anywhere, so it survives, and the snapshot is rebuilt from it
	// alone until the next pull re-folds the full history.
	if (parsed.content !== content) {
		// Same content, older fold rules: every id the snapshot names still exists,
		// and the engine never folds onto a local snapshot, so it is shown until the
		// first pull replaces it. Its cursor is dropped: it covers nothing the
		// server will vouch for under these rules.
		const sameContent =
			typeof parsed.content === "string" && contentOf(parsed.content) === contentOf(content);
		const shown = sameContent ? coerceState(parsed.snapshot) : null;
		if (shown) return { version: LOCAL_VERSION, content, snapshot: shown, coversUpTo: null, tail };
		if (!tail.length) return null;
		return {
			version: LOCAL_VERSION,
			content,
			snapshot: fold(defaultState(), tail),
			coversUpTo: null,
			tail,
		};
	}
	const snapshot = coerceState(parsed.snapshot);
	if (!snapshot) return null;
	const coversUpTo = typeof parsed.coversUpTo === "string" ? parsed.coversUpTo : null;
	return { version: LOCAL_VERSION, content, snapshot, coversUpTo, tail };
}

export const saveLocal = (userId: string, p: LocalProgress): void =>
	write(localKey(userId), JSON.stringify(p));

/**
 * Erase one account's local cache. Exactly one caller: the account-deletion
 * flow. Signing out must NOT call this — the key is kept so that
 * returning to your own account is instant and offline.
 */
export const clearLocal = (userId: string): void => drop(localKey(userId));

/**
 * The remembered session. It holds the email as well as the id because
 * the account surface shows it and the offline branch — which never reaches
 * account.get() — has no other source for it.
 *
 * This is NOT an authorization token and must never be treated as one. It
 * chooses which local progress to display and nothing else; every remote call
 * still needs a real Appwrite session.
 */
export function rememberedUser(): AuthUser | null {
	const raw = read(SESSION_KEY);
	if (!raw) return null;
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!isObj(parsed)) return null;
		const { userId, email, verified } = parsed;
		if (typeof userId !== "string" || typeof email !== "string") return null;
		// Written before verification was tracked: every entry with an email was a real account.
		return { id: userId, email, verified: typeof verified === "boolean" ? verified : email !== "" };
	} catch {
		return null;
	}
}

export const rememberUser = (u: AuthUser): void =>
	write(SESSION_KEY, JSON.stringify({ userId: u.id, email: u.email, verified: u.verified }));
export const forgetUser = (): void => drop(SESSION_KEY);
